import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Typography from '@mui/material/Typography';
import { useHistory } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import GlobalStyles from '@mui/material/GlobalStyles';

import { buildUrl } from 'utils';
import { Card } from 'components/UI';
import { locationActions } from 'state/modules/locations';

import i18n from 'i18n-js';

// The shared v4 Card expects a classes object ({ root }) for its CardContent.
// Define the class globally so the flush padding survives without makeStyles.
const cardContentFlushStyles = (
  <GlobalStyles
    styles={{
      '.lm-location-card-content-root': {
        padding: 0,
        paddingBottom: '0 !important',
      },
    }}
  />
);

const cardContentClasses = { root: 'lm-location-card-content-root' };

const listItemTextSx = {
  display: 'flex',
  justifyContent: 'space-between',
  textAlign: 'right',
};

const listItemTextSlotProps = {
  primary: {
    sx: {
      fontSize: 13,
      fontWeight: 500,
      color: '#4D4F5C',
      textAlign: 'left',
      wordBreak: 'break-word',
    },
  },
  secondary: {
    sx: {
      fontSize: '13px',
      color: '#4D4F5C',
      fontWeight: 300,
      wordBreak: 'break-word',
      maxWidth: '70%',
    },
  },
};

const LocationDetails = ({ id }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { location } = useSelector((state) => state.locations);

  useEffect(() => {
    if (id) {
      dispatch(locationActions.fetchLocationsById(id));
    }
  }, []); // eslint-disable-line

  const goToVehicleEdition = (e) => {
    e.preventDefault();
    history.push(buildUrl(`/locations/${id}/update`));
  };

  const getLocationData = (location = {}) => {
    const locationInfo = [
      { label: i18n.t('locations.zone_name'), value: location.name || '-' },
      { label: i18n.t('locations.address'), value: location?.address || '-' },
      { label: i18n.t('locations.country'), value: location.country || '-' },
      { label: i18n.t('locations.state'), value: location?.state || '-' },
      { label: i18n.t('locations.city'), value: location?.city || '-' },
      { label: i18n.t('locations.zip_code'), value: location?.zipcode || '-' },
      { label: i18n.t('locations.time_zone'), value: location?.time_zone || '-' },
    ];

    return locationInfo;
  };

  return (
    <Card
      title={i18n.t('locations.location_info')}
      headerActionType="vertical"
      contentRootClasses={cardContentClasses}
    >
      <>
        {cardContentFlushStyles}
        <Box sx={{ padding: '24px 32px' }}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid size={8}>
              <Typography variant="h3">
                <Box
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                >
                  ID# {id}
                </Box>
              </Typography>
            </Grid>
            <Grid size={4} sx={{ textAlign: 'right' }}>
              <Typography>
                <Link
                  href="#"
                  data-cy="update-info"
                  onClick={goToVehicleEdition}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'none',
                      fontWeight: 500,
                    },
                  }}
                >
                  {i18n.t('locations.update_info')}
                </Link>
              </Typography>
            </Grid>
          </Grid>
          <Divider
            sx={{
              borderColor: '#EEE',
              marginLeft: '-32px',
              marginRight: '-32px',
              marginTop: '12px',
            }}
          />

          <List
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            {getLocationData(location).map((val, index) => (
              <Fragment key={index}>
                {(val?.condition || val?.condition === undefined) && (
                  <ListItem
                    sx={{
                      width: '100%',
                      padding: '0 4px 0 0',
                      borderBottom: '1px solid #EEE',
                      '&:last-child': {
                        border: 0,
                      },
                    }}
                  >
                    <ListItemText
                      sx={listItemTextSx}
                      slotProps={listItemTextSlotProps}
                      primary={val.label}
                      secondary={val.value}
                    />
                  </ListItem>
                )}
              </Fragment>
            ))}
          </List>
        </Box>
      </>
    </Card>
  );
};

LocationDetails.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default LocationDetails;
